import React, { useState, useEffect } from "react";
import { Table, Tag, message, Button, Spin } from "antd";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import { decodeStr } from "../../../utils";
import ReactJson from '@microlink/react-json-view';
import UserLinks from "../../shared/UserLinks";
import { useIsMobile } from "../../../hooks/useIsMobile";
import UserCard from "./UserCard";
import { useAuth0UserBlocks } from "../../../hooks/useAuth0UserBlocks";
import Auth0UserBlockControl from "../../shared/Auth0UserBlockControl";

const OrgUsers = ({ orgId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(new Set());
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const isMobile = useIsMobile();
  const {
    canManageAuth0Blocks,
    checkUser,
    checkingUserIds,
    unblockUser,
    unlockingUserIds,
  } = useAuth0UserBlocks();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = await getTokenSilently();

        // GraphQL query to fetch users filtered by org_pointer
        const query = `
          query GetOrgUsers($orgId: ID!) {
            users(first: 1000, order: first_name_ASC, where: {org_pointer: {have: {objectId: {equalTo: $orgId}}}}) {
              edges {
                node {
                  objectId
                  first_name
                  last_name
                  telefono
                  ruolo
                  email
                  user_status
                  jira_id
                  username
                }
              }
            }
          }
        `;

        const response = await fetch(
          `${config.baseUrl[isProd ? "prod" : "dev"]}/graphql?raw=true`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              query,
              variables: { orgId },
            }),
          }
        );

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        // Transform the edges/node structure to a flat array
        const usersData = result.users.edges.map((edge) => edge.node);
        setUsers(usersData || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        message.error("Errore nel caricamento degli utenti");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [orgId, getTokenSilently, isProd]);

  const syncUserWithZoho = async (userId) => {
    try {
      setSyncing((prev) => new Set(prev).add(userId));
      const token = await getTokenSilently();
      const response = await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]}/crm/sync/user/${userId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Errore HTTP ${response.status}`);
      }

      message.success("Sincronizzazione avviata con successo");
    } catch (error) {
      console.error("Errore sincronizzazione Zoho:", error);
      message.error("Errore nell'avviare la sincronizzazione");
    } finally {
      setSyncing((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "objectId",
      key: "objectId",
    },
    {
      title: "Nome",
      dataIndex: "first_name",
      key: "first_name",
      render: (text) => decodeStr(text || ""),
    },
    {
      title: "Cognome",
      dataIndex: "last_name",
      key: "last_name",
      render: (text) => decodeStr(text || ""),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => text || "-",
    },
    {
      title: "Telefono",
      dataIndex: "telefono",
      key: "telefono",
      render: (text) => text || "-",
    },
    {
      title: "Ruolo",
      dataIndex: "ruolo", 
      key: "ruolo",
      render: (ruolo) => {
        if (!ruolo) return <Tag color="default">-</Tag>;

        const colors = {
          Amministratore: "purple",
          Collaboratore: "geekblue",
        };

        return <Tag color={colors[ruolo] || "blue"}>{ruolo}</Tag>;
      },
    },
    {
      title: "Stato",
      dataIndex: "user_status", 
      key: "user_status",
      render: (userStatus) => {
        return (
          <Tag color={userStatus ? "green" : "volcano"}>
            {userStatus ? "ATTIVO" : "DISATTIVO"}
          </Tag>
        );
      },
    },
    ...(canManageAuth0Blocks
      ? [
          {
            title: "Sblocco Auth0",
            key: "auth0_block",
            render: (_, record) => (
              <Auth0UserBlockControl
                user={record}
                checking={checkingUserIds.has(record.objectId)}
                unlocking={unlockingUserIds.has(record.objectId)}
                onCheck={checkUser}
                onUnblock={unblockUser}
              />
            ),
          },
        ]
      : []),
    {
      title: "Links",
      dataIndex: "objectId",
      key: "links",
      align: "center",
      render: (_, record) => {
        return <UserLinks user={record} />;
      },
    },
  ];

  return (
    <div className="org-users-container">
      {isMobile ? (
        // Mobile: Card layout
        <Spin spinning={loading}>
          <div className="user-cards-container">
            {users.map((user) => (
              <UserCard
                key={user.objectId}
                user={user}
                onSync={syncUserWithZoho}
                syncing={syncing.has(user.objectId)}
                canManageAuth0Blocks={canManageAuth0Blocks}
                auth0Checking={checkingUserIds.has(user.objectId)}
                auth0Unlocking={unlockingUserIds.has(user.objectId)}
                onAuth0Check={checkUser}
                onAuth0Unblock={unblockUser}
              />
            ))}
            {users.length === 0 && !loading && (
              <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
                Nessun utente trovato
              </div>
            )}
          </div>
        </Spin>
      ) : (
        // Desktop: Table layout
        <Table
          dataSource={users}
          columns={columns}
          rowKey="objectId"
          loading={loading}
          pagination={false}
          scroll={{ x: true }}
          expandable={{
            expandedRowRender: (record) => (
              <div>
                <ReactJson src={record} name={null} />
                <div style={{ marginTop: 12 }}>
                  <Button
                    type="primary"
                    onClick={() => syncUserWithZoho(record.objectId)}
                    loading={syncing.has(record.objectId)}
                  >
                    Sincronizza utente con CRM Zoho
                  </Button>
                </div>
              </div>
            ),
            rowExpandable: (record) => record,
          }}
        />
      )}
    </div>
  );
};

export default OrgUsers;
